<template>
  <div
    style="
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 16px;
    "
  >
    <a-typography-title :level="4" style="margin: 0">
      Reglas de notificación
    </a-typography-title>
    <a-button :disabled="actionLoading" type="primary" @click="handleCreate">
      Nueva regla
    </a-button>
  </div>
  <div>
    <a-modal
      :wrapClassName="'modal-md'"
      v-model:visible="showForm"
      :footer="null"
      @cancel="handleCancel"
      @on-close="handleCancel"
    >
      <CompanyNotificationRulesForm
        v-if="context === 'company'"
        :types="types"
        :groups="groups"
        :policies="policies"
        :rule="selectedRule"
        :loading="loading"
        :submitting="submitting"
        :error="submittingError"
        :success="success"
        @save="handleSave"
        @cancel="handleCancel"
        ref="formRef"
      />
    </a-modal>

    <a-alert
      v-for="(err, index) in errors"
      :key="index"
      type="error"
      :message="err"
      style="margin-bottom: 16px"
    />

    <CompanyNotificationRulesList
      v-if="context === 'company'"
      :types="types"
      :rules="rules"
      :loading="loading"
      :action-loading="actionLoading"
      @edit="handleEdit"
      @delete="handleDelete"
    />
  </div>
</template>

<script setup>
import CompanyNotificationRulesList from './company/notification-rule-list.vue';
import CompanyNotificationRulesForm from './company/notification-rule-form.vue';
import { ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import {
  COMPANY_NOTIFICATION_RULES_FETCH_TYPES,
  COMPANY_NOTIFICATION_RULES_FETCH_RULES,
  COMPANY_GROUP_COMPANIES_FETCH_GROUPS,
  COMPANY_POLICIES_FETCH_POLICIES,
  COMPANY_NOTIFICATION_RULES_GET_TYPES,
  COMPANY_NOTIFICATION_RULES_GET_RULES,
  COMPANY_NOTIFICATION_RULES_GET_LOADING,
  COMPANY_NOTIFICATION_RULES_GET_ERROR,
  COMPANY_GROUP_COMPANIES_GET_GROUPS,
  COMPANY_POLICIES_GET_POLICIES,
  COMPANY_GROUP_COMPANIES_GET_LOADING,
  COMPANY_POLICIES_GET_LOADING,
  COMPANY_GROUP_COMPANIES_GET_ERROR,
  COMPANY_POLICIES_GET_ERROR,
  COMPANY_NOTIFICATION_RULES_DELETE_RULE,
  COMPANY_NOTIFICATION_RULES_CREATE_RULE,
  COMPANY_NOTIFICATION_RULES_UPDATE_RULE,
  COMPANY_NOTIFICATION_RULES_CLEAR_ERROR
} from '@/store/types';

const store = useStore();
const route = useRoute();

const context = computed(() => {
  if (route.path.includes('/clientes/')) return 'company';
  if (route.path.includes('/aseguradoras/')) return 'insurance-company';
  return null;
});

const formRef = ref(null);
const showForm = ref(false);
const success = ref('');
const selectedRule = ref(null);

const rawGroups = computed(
  () => store.getters[COMPANY_GROUP_COMPANIES_GET_GROUPS] || []
);
const rawPolicies = computed(
  () => store.getters[COMPANY_POLICIES_GET_POLICIES] || []
);
const rawTypes = computed(
  () => store.getters[COMPANY_NOTIFICATION_RULES_GET_TYPES] || []
);
const rawRules = computed(
  () => store.getters[COMPANY_NOTIFICATION_RULES_GET_RULES] || []
);

const groupsLoading = computed(
  () => store.getters[COMPANY_GROUP_COMPANIES_GET_LOADING]
);
const policiesLoading = computed(
  () => store.getters[COMPANY_POLICIES_GET_LOADING]
);
const rulesLoading = computed(
  () => store.getters[COMPANY_NOTIFICATION_RULES_GET_LOADING]
);

const groupsError = computed(
  () => store.getters[COMPANY_GROUP_COMPANIES_GET_ERROR]
);
const policiesError = computed(() => store.getters[COMPANY_POLICIES_GET_ERROR]);
const rulesError = computed(
  () => store.getters[COMPANY_NOTIFICATION_RULES_GET_ERROR]
);

const rules = computed(() => {
  return rawRules.value.map((rule) => {
    const enrichedRule = { ...rule };

    if (rule.groupCompanyId) {
      enrichedRule.groupCompany = rawGroups.value.find(
        (g) => g.id === rule.groupCompanyId
      );
    }

    if (rule.policyId) {
      enrichedRule.policy = rawPolicies.value.find(
        (p) => p.id === rule.policyId
      );
    }

    return enrichedRule;
  });
});

const types = computed(() => {
  const defaultType = {
    id: null,
    description: 'Global'
  };
  return [defaultType, ...rawTypes.value];
});

const groups = computed(() => {
  const defaultGroup = {
    id: null,
    name: 'Global'
  };
  return [defaultGroup, ...rawGroups.value];
});

const policies = computed(() => {
  const defaultPolicy = {
    id: null,
    numberPolicy: 'Global'
  };
  return [defaultPolicy, ...rawPolicies.value];
});

const errors = computed(() => {
  return [
    rulesError.value.fetchRules,
    rulesError.value.fetchTypes,
    rulesError.value.deleteRule,
    groupsError.value,
    policiesError.value
  ].filter((err) => err);
});

const actionLoading = computed(
  () =>
    rulesLoading.value.createRule ||
    rulesLoading.value.editRule ||
    rulesLoading.value.deleteRule
);

const submitting = computed(() => {
  return rulesLoading.value.createRule || rulesLoading.value.updateRule;
});

const submittingError = computed(() => {
  return rulesError.value.createRule || rulesError.value.updateRule;
});

const loading = computed(
  () =>
    rulesLoading.value.fetchRules ||
    rulesLoading.value.fetchTypes ||
    groupsLoading.value ||
    policiesLoading.value
);

const handleCreate = () => {
  selectedRule.value = null;
  showForm.value = true;
};

const handleEdit = (rule) => {
  selectedRule.value = rule;
  showForm.value = true;
};

const handleDelete = (rule) => {
  store.dispatch(COMPANY_NOTIFICATION_RULES_DELETE_RULE, {
    id: rule.id,
    companyId: route.params.id
  });
};

const handleSave = async (rule) => {
  success.value = '';
  try {
    if (selectedRule.value) {
      await store.dispatch(COMPANY_NOTIFICATION_RULES_UPDATE_RULE, {
        companyId: route.params.id,
        id: selectedRule.value.id,
        ...rule
      });
      success.value = 'Regla actualizada correctamente';
    } else {
      await store.dispatch(COMPANY_NOTIFICATION_RULES_CREATE_RULE, {
        companyId: route.params.id,
        ...rule
      });
      success.value = 'Regla creada correctamente';
    }
  } catch (err) {
    console.error('Error saving rule:', err);
  }
};

const handleCancel = async () => {
  if (selectedRule.value) {
    await store.dispatch(COMPANY_NOTIFICATION_RULES_CLEAR_ERROR, {
      type: 'updateRule'
    });
  } else {
    await store.dispatch(COMPANY_NOTIFICATION_RULES_CLEAR_ERROR, {
      type: 'createRule'
    });
  }

  showForm.value = false;
  selectedRule.value = null;
  success.value = '';

  if (formRef.value && formRef.value.resetForm) {
    await formRef.value.resetForm();
  }
};

onMounted(() => {
  if (context.value === 'company') {
    store.dispatch(COMPANY_NOTIFICATION_RULES_FETCH_TYPES);
    store.dispatch(COMPANY_NOTIFICATION_RULES_FETCH_RULES, {
      companyId: route.params.id
    });
    store.dispatch(COMPANY_GROUP_COMPANIES_FETCH_GROUPS, {
      companyId: route.params.id
    });
    store.dispatch(COMPANY_POLICIES_FETCH_POLICIES, {
      companyId: route.params.id
    });
  }
});
</script>
